// src/Component/FAQ.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, HelpCircle, MessageCircle, HeadphonesIcon } from 'lucide-react';

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0); // First one open by default

  // Updated FAQs for Motilal Algo & Indian Stock Market context
  const faqs = [
    {
      question: "What segments can I trade using Motilal Algo?",
      answer: "You can seamlessly trade across Equity, Futures & Options (Nifty/BankNifty), Commodities (MCX), and Currencies on both NSE and BSE. Our platform supports all major segments with zero-latency execution."
    },
    {
      question: "Do I need coding skills to use the Algo platform?",
      answer: "Not at all! Motilal Algo is designed for both beginners and pros. We offer pre-built, plug-and-play strategies. You just need to select a strategy, define your risk, and start trading with a single click."
    },
    {
      question: "Are there any hidden API charges?",
      answer: "No, API access is completely free for all our active clients. We believe in 100% transparency. You only pay your standard, flexible brokerage plan with ₹0 AMC charges."
    },
    {
      question: "How fast is the trade execution?",
      answer: "Speed is our edge. Our servers are co-located near the exchange, providing ultra-low latency. Your orders are executed in microseconds, ensuring you get the exact price you targeted during high volatility."
    },
    {
      question: "How do I open a Demat account and get started?",
      answer: "It's a completely paperless process. Just click on 'Open Demat Account', complete your e-KYC using your Aadhar and PAN in under 5 minutes. Your account will be activated within 24 hours."
    },
    {
      question: "Can I do manual trading along with Algo?",
      answer: "Absolutely! You get a powerful all-in-one dashboard. You can let the algorithms handle your BankNifty options while you manually invest in your favorite long-term equity stocks simultaneously."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="relative py-20 md:py-32 bg-slate-50 overflow-hidden font-[Urbanist]">
      
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          
          {/* =========================================
              LEFT COLUMN: Header & Support (Sticky)
              ========================================= */}
          <div className="lg:w-1/3">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:sticky lg:top-32"
            >
              <div className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-green-100 text-green-700 text-sm font-bold tracking-wide shadow-sm border border-green-200 mb-6">
                <HelpCircle size={16} /> FAQ
              </div>
              
              <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-4">
                Got questions? <br className="hidden lg:block"/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800">We've got answers.</span>
              </h2>
              
              <p className="text-slate-600 text-base md:text-lg font-medium mb-10">
                Everything you need to know about Motilal Algo and how we can help you become a profitable trader.
              </p>

              {/* Support Card */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-xl shadow-slate-200/50 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-full blur-3xl -mr-10 -mt-10 transition-transform group-hover:scale-150" />
                
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-green-100 text-green-600 rounded-xl flex items-center justify-center mb-4">
                    <HeadphonesIcon size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Still have questions?</h3>
                  <p className="text-slate-500 text-sm font-medium mb-6">
                    Can’t find the answer you’re looking for? Our support team is here to help you 24/7.
                  </p>
                  <button className="w-full inline-flex justify-center items-center gap-2 bg-slate-900 hover:bg-green-600 text-white px-6 py-3.5 rounded-xl font-bold transition-colors shadow-md">
                    <MessageCircle size={18} /> Chat with us
                  </button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* =========================================
              RIGHT COLUMN: Accordion List
              ========================================= */}
          <div className="lg:w-2/3">
            <div className="space-y-4 md:space-y-5">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`border rounded-2xl transition-all duration-300 overflow-hidden bg-white ${
                    openIndex === index 
                      ? 'border-blue-500 shadow-[0_10px_30px_-10px_rgba(37,99,235,0.2)]' 
                      : 'border-slate-200 hover:border-blue-300 shadow-sm'
                  }`}
                >
                  {/* Question Button */}
                  <button
                    className="w-full flex justify-between items-center p-5 md:p-6 text-left focus:outline-none"
                    onClick={() => toggleFAQ(index)}
                  >
                    <span className={`text-base md:text-lg font-extrabold pr-4 transition-colors ${
                      openIndex === index ? 'text-green-700' : 'text-slate-800'
                    }`}>
                      {faq.question}
                    </span>
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      openIndex === index ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {openIndex === index ? <Minus size={18} /> : <Plus size={18} />}
                    </div>
                  </button>

                  {/* Answer Content (Animated) */}
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                      >
                        <div className="px-5 md:px-6 pb-6 text-slate-600 font-medium leading-relaxed">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default FAQ;